import React, { useEffect, useState } from "react";
import { useContent } from "../../hooks/useContent";
import { useLanguage } from "../../contexts/LanguageContext";
import type { ChallengeRequestContentProps } from "../../content/component-challenge-request";
import { Modal } from "../ui/Modal";
import { ContactRequestForm } from "../contactRequest/ContactRequestForm";

export type ChallengeRequestModalProps = {
    isOpen: boolean;
    onClose: () => void;
    /** Vorbelegtes Thema aus der aufrufenden Sektion (Entscheid C5). */
    topic: string;
    recipientEmail?: string;
    recipientName?: string;
    /** Wessen Kalender die freien Zeiten liefert (Entscheid C5). */
    calendarId?: string;
    /**
     * Kennung der Kampagnenseite, z.B. `ausschreibungen`. Das Formular meldet
     * sie als `campaign_page` an der Conversion `contact_request` (QUAWEB-42).
     */
    campaignPage?: string;
    /**
     * Einleitung des Dialogs fuer diese Seite. Ohne Angabe gilt `intro` aus
     * `components/challenge-request.mdx` (QUAWEB-40).
     */
    dialogIntro?: string;
};

/**
 * Der Challenge-Dialog der RFP-Kampagnenseite (21.9.2026).
 *
 * EIN ANDERES GESICHT, DERSELBE KANAL: Das Formular ist `ContactRequestForm`,
 * unveraendert und ungeforkt. Es nimmt seinen gesamten Text als `content`
 * entgegen und haelt keine eigene Zeichenkette — deshalb genuegt es, ihm den
 * Inhalt der Komponente `challengeRequest` zu geben. Versandweg
 * (`submitContactRequest`), Empfaenger und Tracking bleiben damit dieselben.
 *
 * Warum dann ueberhaupt eine zweite Komponente? Weil der Kontakt-Dialog auf 46
 * Seiten mit demselben Wortlaut spricht und das so bleiben soll. Die
 * Kampagnenseite laedt zu einer «Challenge» ein; «Kontakt aufnehmen» waere
 * dort die falsche Ueberschrift. Der Preis ist ein zweites MDX, der Gewinn:
 * die 46 Seiten sehen von dieser Aenderung keine Zeile.
 *
 * Dass der Challenge-Inhalt in das Formular passt, ist kein Zufall und keine
 * Umgehung: Beide Schemata fuehren denselben vollen Feldsatz, also ist der
 * eine Typ dem anderen strukturell zuweisbar. Faellt in `challengeRequest`
 * spaeter ein Feld weg, meldet es der Typecheck hier — nicht der Besucher.
 */
export const ChallengeRequestModal: React.FC<ChallengeRequestModalProps> = ({
    isOpen,
    onClose,
    topic,
    recipientEmail,
    recipientName,
    calendarId,
    campaignPage,
    dialogIntro,
}) => {
    const { getComponentContent, getSectionContent } = useContent();
    const { language } = useLanguage();
    const content = getComponentContent<ChallengeRequestContentProps>("challengeRequest");
    const [isSubmitted, setIsSubmitted] = useState(false);

    /*
     * Rueckfall wie im Kontakt-Dialog: die GLOBALE KONTAKT-SEKTION. Die
     * aufrufende `ClosingCtaSection` fuehrt keine `person`, und der
     * Ansprechpartner der Challenge ist derselbe wie ueberall sonst. Wer ihn
     * wechselt, aendert weiterhin EINE Stelle.
     */
    const globalContact = getSectionContent<{
        person?: { name: string; email: string };
    }>("contact");
    const fallbackPerson = globalContact?.person;

    // Beim erneuten Oeffnen wieder das Formular zeigen, nicht die Danke-Ansicht
    // des letzten Mals.
    useEffect(() => {
        if (isOpen) {
            setIsSubmitted(false);
        }
    }, [isOpen]);

    // Ohne Inhalt kein Dialog: ein leerer Dialog waere schlimmer als keiner.
    if (!content) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isSubmitted ? content.thankYouTitle : content.title}>
            {isSubmitted ? (
                <div className="space-y-6">
                    <p className="text-gray-700">{content.thankYouMessage}</p>
                    <button
                        type="button"
                        onClick={onClose}
                        // eslint-disable-next-line max-len
                        className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-dark"
                    >
                        {content.close}
                    </button>
                </div>
            ) : (
                <ContactRequestForm
                    content={dialogIntro ? { ...content, intro: dialogIntro } : content}
                    topic={topic}
                    recipientEmail={recipientEmail ?? fallbackPerson?.email}
                    recipientName={recipientName ?? fallbackPerson?.name}
                    calendarId={calendarId ?? fallbackPerson?.email}
                    campaignPage={campaignPage}
                    language={language === "en" ? "en" : "de"}
                    onSuccess={() => setIsSubmitted(true)}
                    onCancel={onClose}
                />
            )}
        </Modal>
    );
};

export default ChallengeRequestModal;
